import React, { useState } from 'react'
import { Plus, FolderPlus, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { apiClient } from '@/services/api'

interface TemplateCategoryManagerProps {
  categories: Array<{
    id: string
    name: string
    description: string
    sortOrder: number
  }>
  selectedCategory: string
  onCategoryChange: (categoryId: string) => void
  onCategoriesUpdate: () => void
  error?: string
  className?: string
}

export const TemplateCategoryManager: React.FC<TemplateCategoryManagerProps> = ({
  categories,
  selectedCategory,
  onCategoryChange,
  onCategoriesUpdate,
  error,
  className
}) => {
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [isCreatingCategory, setIsCreatingCategory] = useState(false)

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      alert('Please enter a category name')
      return
    }

    setIsCreatingCategory(true)
    try {
      const newCategory = await apiClient.post('/templates/user/categories', {
        name: newCategoryName.trim(),
        description: `${newCategoryName.trim()} materials`
      })
      
      // Refresh the categories list
      onCategoriesUpdate()
      onCategoryChange(newCategory.id)
      setNewCategoryName('')
      setShowAddCategory(false)
      alert('Category created successfully!')
    } catch (error) {
      console.error('Error creating category:', error)
      alert('Error creating category. Please try again.')
    } finally {
      setIsCreatingCategory(false)
    }
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Category *
        </label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowAddCategory(!showAddCategory)}
          leftIcon={<FolderPlus className="w-3 h-3" />}
        >
          Add Category
        </Button>
      </div>
      
      {showAddCategory && (
        <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex gap-2">
            <Input
              type="text"
              value={newCategoryName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCategoryName(e.target.value)}
              placeholder="Enter category name..."
              className="flex-1"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleCreateCategory()
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              onClick={handleCreateCategory}
              disabled={isCreatingCategory || !newCategoryName.trim()}
              leftIcon={isCreatingCategory ? undefined : <Plus className="w-3 h-3" />}
            >
              {isCreatingCategory ? 'Creating...' : 'Create'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowAddCategory(false)
                setNewCategoryName('')
              }}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
      
      <Select
        value={selectedCategory}
        onValueChange={onCategoryChange}
        className={error ? 'border-red-500' : ''}
        required
      >
        <option value="">Select category...</option>
        {categories.map(category => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </Select>
      {error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}
      {categories.length === 0 && (
        <p className="text-sm text-gray-500 mt-1">
          No categories found. Click "Add Category" to create your first category.
        </p>
      )}
    </div>
  )
}